"use client";

import { CarePlanTagDisplay } from "@/components/care-plan/tag-display";

import { tv } from "@/lib/tailwind-utils";
import { cx } from "@/lib/tailwind-utils";

import type { CarePlanTagDetail } from "@/types/care-plan-template";

import type { CarePlanSize, Orientation } from "./types";

type CarePlanPreviewCardChildren =
  | React.ReactNode
  | ((contentClassName: string) => React.ReactNode);

interface CarePlanPreviewCardProps {
  name: string;
  stepCount: number;
  completedStepCount?: number;
  tags?: CarePlanTagDetail[];
  size?: CarePlanSize;
  orientation?: Orientation;
  actions?: React.ReactNode;
  children?: CarePlanPreviewCardChildren;
}

const previewCard = tv({
  slots: {
    root: "overflow-x-hidden rounded-lg border border-border bg-background gap-2 relative",
    panel:
      "flex-shrink-0 absolute left-0 top-0 bg-background rounded-ee-xl border-r border-b py-2 px-4 z-20",
    title: "font-medium text-foreground line-clamp-2 w-full text-left",
    content: "block overflow-x-auto relative z-10 w-full shrink-1",
  },
  variants: {
    size: {
      sm: { root: "px-3 py-2", title: "text-caption" },
      md: { root: "px-4 py-3", title: "text-body" },
    },
    hasActions: { true: {}, false: {} },
    orientation: {
      horizontal: {},
      vertical: { content: "pt-24" },
    },
  },
  compoundVariants: [
    { size: "sm", hasActions: true, class: { panel: "w-32" } },
    { size: "md", hasActions: true, class: { panel: "w-56" } },
    { size: "sm", hasActions: false, class: { panel: "w-24" } },
    { size: "md", hasActions: false, class: { panel: "w-32" } },
    {
      size: "sm",
      hasActions: true,
      orientation: "horizontal",
      class: { content: "pl-32" },
    },
    {
      size: "md",
      hasActions: true,
      orientation: "horizontal",
      class: { content: "pl-56" },
    },
    {
      size: "sm",
      hasActions: false,
      orientation: "horizontal",
      class: { content: "pl-24" },
    },
    {
      size: "md",
      hasActions: false,
      orientation: "horizontal",
      class: { content: "pl-32" },
    },
  ],
});

/**
 * A read-only card shell for previewing care plans.
 * Shows name, tags, and progress in a left panel with content scrollable to the right.
 * Used for empty states and template previews where interactive elements aren't needed.
 */
export function CarePlanPreviewCard({
  name,
  stepCount,
  completedStepCount = 0,
  tags,
  orientation = "horizontal",
  size = "md",
  actions,
  children,
}: CarePlanPreviewCardProps) {
  const progressPercent =
    stepCount > 0 ? Math.round((completedStepCount / stepCount) * 100) : 0;

  const { root, panel, title, content } = previewCard({
    size,
    hasActions: !!actions,
    orientation,
  });
  const contentClassName = content();

  return (
    <div className={root()}>
      <div className={panel()}>
        <div className="flex items-center gap-1 w-full">
          <h4 className={title()}>{name}</h4>
          {actions}
        </div>
        {tags && tags.length > 0 && <CarePlanTagDisplay tags={tags} />}
        <div className="mt-0.5 flex items-center gap-2">
          <p className="text-caption text-foreground-secondary">
            {stepCount} step<span>{stepCount !== 1 ? "s" : ""}</span> ·{" "}
            <span
              className={cx(
                progressPercent === 100
                  ? "text-status-success-text"
                  : progressPercent > 0
                    ? "text-accent-foreground"
                    : "text-foreground-secondary",
              )}
            >
              {progressPercent === 100 ? "Complete" : `${progressPercent}%`}
            </span>
          </p>
        </div>
      </div>
      {typeof children === "function" ? (
        children(contentClassName)
      ) : (
        <div className={contentClassName}>{children}</div>
      )}
    </div>
  );
}
